import './styles.scss';
// AG Grid modules are registered once via the shared rulesAgGridModules import in RulesConfig.
import React, { useState, useMemo, useContext, useCallback, useRef } from 'react';
import type { GridApi } from 'ag-grid-community';
import { AgGridBase, useClientExternalTextSearch } from 'Components/organisms/AgGridBase';
import { Button, Modal, Form, Row, Col } from 'react-bootstrap';
import { RiCloseLine } from '@react-icons/all-files/ri/RiCloseLine';
import { ConfirmationModal } from 'Components/molecules';
import { DropdownComponent } from 'Components/molecules';
import {
    YES_DELETE_IT,
    DEFAULT_ERROR_MSG,
    RULESETS_SEARCH_PLACEHOLDER,
    RULESETS_ADD_BTN,
    RULESETS_REFRESH_BTN,
    RULESETS_DELETE_MODAL_TITLE,
    RULESETS_DELETE_MODAL_BODY,
    RULESETS_MODAL_EDIT_TITLE,
    RULESETS_MODAL_CREATE_TITLE,
    RULESETS_LABEL_NAME,
    RULESETS_LABEL_CONNECTOR,
    RULESETS_LABEL_DESCRIPTION,
    RULESETS_NAME_PLACEHOLDER,
    RULESETS_DESC_PLACEHOLDER,
    RULESETS_NONE_OPTION,
    RULESETS_DEFAULT_TOGGLE_LABEL,
    RULESETS_DEFAULT_TOGGLE_ON_HINT,
    RULESETS_DEFAULT_TOGGLE_OFF_HINT,
    RULESETS_CANCEL_BTN,
    RULESETS_SAVING,
    RULESETS_SAVE_CHANGES,
    RULESETS_CREATE_BTN,
    RULESETS_DELETED,
    RULESETS_UPDATED,
    RULESETS_CREATED,
} from 'Core-utils/constants/constants';
import { useI18n } from 'Hooks/useI18n';
import { ToastsContext } from 'Rdx/contexts/ToastsContext';
import { useConnectorData } from 'Hooks/useConnectorsData';
import { createRuleSet, updateRuleSetById, deleteRuleSetById } from 'Apis/library';
import { RuleSetDto, RuleSetFormState } from 'Core-utils/types/RuleSetsConfig.types';
import { emptyRuleSetForm, formFromRuleSet } from 'Core-utils/helpers/ruleSetHelpers';
import { getRuleSetsGridColumns } from './getRuleSetsGridColumns';

// ─── Stable getRowId (defined outside component to avoid recreation per render) ──
const getRowId = (params: { data?: RuleSetDto }) => String(params.data?.id ?? '');

interface RuleSetsConfigProps {
    connectorMap: Record<string, string>;
    ruleSetsList: RuleSetDto[];
    isRuleSetsLoading: boolean;
    onRefreshRuleSets: () => void;
    // Called after any successful create / update / delete so the parent
    // revalidates the shared rulesets SWR cache (Rules + Rule Sets tabs).
    onRuleSetMutated?: (updated?: RuleSetDto) => void;
}

const RuleSetsConfig = ({
    connectorMap,
    ruleSetsList,
    isRuleSetsLoading,
    onRefreshRuleSets,
    onRuleSetMutated,
}: RuleSetsConfigProps) => {
    const { t } = useI18n();
    const { addToast } = useContext(ToastsContext);
    const { extractedData: connectorsList } = useConnectorData();

    const [saving, setSaving] = useState(false);

    const ruleSets: RuleSetDto[] = useMemo(
        () => (Array.isArray(ruleSetsList) ? ruleSetsList : []),
        [ruleSetsList],
    );

    // ── Grid API ref + external search ────────────────────────────────────────
    const gridApiRef = useRef<GridApi<RuleSetDto> | null>(null);

    // Ref so cell renderers always read the latest connector map without
    // requiring useMemo re-creation of column definitions.
    const connectorMapRef = useRef(connectorMap);
    connectorMapRef.current = connectorMap;

    const { handleSearchChange, isExternalFilterPresent, doesExternalFilterPass } =
        useClientExternalTextSearch<RuleSetDto>({
            fields: [
                (row) => row.name ?? '',
                (row) => row.id ?? '',
                (row) => row.description ?? '',
                (row) => (row.cid ? connectorMapRef.current[row.cid] ?? row.cid : ''),
            ],
            onFilterChanged: () => gridApiRef.current?.onFilterChanged(),
        });

    // ── Delete ─────────────────────────────────────────────────────────────────
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [ruleSetToDelete, setRuleSetToDelete] = useState<string | null>(null);

    const handleDeleteClick = useCallback((id: string) => {
        setRuleSetToDelete(id);
        setShowDeleteModal(true);
    }, []);

    const confirmDelete = useCallback(async () => {
        if (!ruleSetToDelete) {
            return;
        }
        try {
            await deleteRuleSetById(ruleSetToDelete);
            addToast({ severity: 'success', body: t(RULESETS_DELETED, 'Rule Set deleted successfully') });
            onRuleSetMutated?.();
        } catch {
            addToast({ severity: 'error', body: DEFAULT_ERROR_MSG });
        } finally {
            setShowDeleteModal(false);
            setRuleSetToDelete(null);
        }
    }, [ruleSetToDelete, addToast, t, onRuleSetMutated]);

    // ── Create / Edit modal ────────────────────────────────────────────────────
    const [showModal, setShowModal] = useState(false);
    const [editingRuleSet, setEditingRuleSet] = useState<RuleSetDto | null>(null);
    const [form, setForm] = useState<RuleSetFormState>(emptyRuleSetForm());

    const patch = (p: Partial<RuleSetFormState>) => setForm((f) => ({ ...f, ...p }));

    const handleOpenModal = useCallback((rs: RuleSetDto | null = null) => {
        setEditingRuleSet(rs);
        setForm(rs ? formFromRuleSet(rs) : emptyRuleSetForm());
        setShowModal(true);
    }, []);

    const handleCloseModal = useCallback(() => {
        setShowModal(false);
        setEditingRuleSet(null);
    }, []);

    const isFormValid = form.name.trim().length > 0;

    const handleSave = useCallback(async () => {
        setSaving(true);
        try {
            if (editingRuleSet) {
                const res = await updateRuleSetById(editingRuleSet.id, {
                    name: form.name,
                    description: form.description || null,
                    cid: form.cid || null,
                    // eslint-disable-next-line camelcase
                    is_default: form.is_default,
                });
                addToast({ severity: 'success', body: t(RULESETS_UPDATED, 'Rule Set updated successfully') });
                // Use the server response for the optimistic update so the list
                // reflects exactly what the server stored (not just form fields).
                const serverRow: RuleSetDto = (res as any)?.data ?? {
                    ...editingRuleSet,
                    name: form.name,
                    description: form.description || null,
                    cid: form.cid || null,
                    // eslint-disable-next-line camelcase
                    is_default: form.is_default,
                };
                onRuleSetMutated?.(serverRow);
            } else {
                const res = await createRuleSet({
                    name: form.name,
                    description: form.description || null,
                    cid: form.cid || null,
                    // eslint-disable-next-line camelcase
                    is_default: form.is_default,
                });
                addToast({ severity: 'success', body: t(RULESETS_CREATED, 'Rule Set created successfully') });
                // Pass the newly created row from the server response so it
                // appears in the list immediately without waiting for SWR refetch.
                const created: RuleSetDto | undefined = (res as any)?.data;
                onRuleSetMutated?.(created);
            }
            handleCloseModal();
        } catch {
            addToast({ severity: 'error', body: DEFAULT_ERROR_MSG });
        } finally {
            setSaving(false);
        }
    }, [form, editingRuleSet, addToast, handleCloseModal, t, onRuleSetMutated]);

    // ── Stable callback refs for column defs ──────────────────────────────────
    const handleOpenModalRef = useRef(handleOpenModal);
    handleOpenModalRef.current = handleOpenModal;

    const handleDeleteClickRef = useRef(handleDeleteClick);
    handleDeleteClickRef.current = handleDeleteClick;

    // ── Column definitions (extracted to getRuleSetsGridColumns.tsx) ──────────
    const columnDefs = useMemo(
        () => getRuleSetsGridColumns(t, connectorMapRef, handleOpenModalRef, handleDeleteClickRef),
        [t],
    );

    const connectorDropdownItems = useMemo(
        () => [
            { id: '', displayValue: t(RULESETS_NONE_OPTION, '— None —'), value: '' },
            ...(connectorsList || []).map((c) => ({
                id: c.cid,
                displayValue: c.title || c.cid,
                value: c.cid,
            })),
        ],
        [connectorsList, t],
    );

    return (
        <div className="d-flex flex-column h-100 ps-0 pe-3">
            {/* ── Toolbar ──────────────────────────────────────────────────── */}
            <div className="d-flex align-items-center gap-3 rulesets-config__toolbar">
                <input
                    type="text"
                    className="form-control form-control-sm rulesets-config__search-input"
                    placeholder={t(RULESETS_SEARCH_PLACEHOLDER, 'Search by name, id, description, connector…')}
                    onChange={(e) => handleSearchChange(e.target.value)}
                />
                <Button
                    size="sm"
                    className="d-flex align-items-center gap-2 text-white rounded-2 rulesets-config__add-btn"
                    onClick={() => handleOpenModal()}
                >
                    {t(RULESETS_ADD_BTN, '+ Add Rule Set')}
                </Button>
                <Button
                    className="d-flex align-items-center gap-2 ms-auto rules-config__refresh-btn"
                    size="sm"
                    onClick={onRefreshRuleSets}
                    disabled={isRuleSetsLoading}
                >
                    {t(RULESETS_REFRESH_BTN, '↻ Refresh')}
                </Button>
            </div>

            {/* ── Grid ─────────────────────────────────────────────────────── */}
            <div className="rulesets-config__table-wrapper">
                <AgGridBase.Root fullWidth>
                    <AgGridBase.Header hidden />
                    <AgGridBase.Table<RuleSetDto>
                        columnDefs={columnDefs}
                        dataMode="client"
                        rowData={ruleSets}
                        showSidebar={false}
                        movableColumns={false}
                        wrapperClassName="rules-ag-grid__wrapper--theme-findings ag-findings-grid__wrapper ag-grid-theme--amp-dark"
                        defaultColDef={{
                            wrapHeaderText: false,
                            autoHeaderHeight: false,
                            minWidth: 80,
                            cellStyle: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
                        }}
                        agGridProps={{
                            loading: isRuleSetsLoading,
                            getRowId,
                            isExternalFilterPresent,
                            doesExternalFilterPass,
                            onGridReady: (e) => { gridApiRef.current = e.api; },
                            onGridPreDestroyed: () => { gridApiRef.current = null; },
                            suppressContextMenu: true,
                            animateRows: false,
                            rowHeight: 72,
                        }}
                    />
                </AgGridBase.Root>
            </div>

            {/* ── Delete confirmation modal ────────────────────────────────── */}
            <ConfirmationModal
                modalTitle={t(RULESETS_DELETE_MODAL_TITLE, 'Delete Rule Set')}
                modalBodyTitle={t(
                    RULESETS_DELETE_MODAL_BODY,
                    'Are you sure you want to delete this rule set? This action cannot be undone.',
                )}
                onPrimaryButtonClick={confirmDelete}
                primaryButtonLabel={t(YES_DELETE_IT, 'Yes, Delete It')}
                show={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false);
                    setRuleSetToDelete(null);
                }}
                buttonVariant="danger"
            />

            {/* ── Create / Edit modal ──────────────────────────────────────── */}
            <Modal
                show={showModal}
                onHide={handleCloseModal}
                size="lg"
                centered
                contentClassName="bg-body-color border-secondary rounded-3"
            >
                <Modal.Header className="border-secondary px-4 py-3 ruleset-modal__header">
                    <div className="d-flex align-items-center gap-3 flex-grow-1">
                        <span className="ruleset-modal__icon">⊞</span>
                        <span className="ruleset-modal__title">
                            {editingRuleSet
                                ? t(RULESETS_MODAL_EDIT_TITLE, 'Edit Rule Set')
                                : t(RULESETS_MODAL_CREATE_TITLE, 'New Rule Set')}
                        </span>
                        {editingRuleSet && (
                            <code className="ruleset-modal__id-badge">{editingRuleSet.id}</code>
                        )}
                    </div>
                    <RiCloseLine
                        size={22}
                        className="ruleset-modal__close-icon"
                        onClick={handleCloseModal}
                    />
                </Modal.Header>

                <Modal.Body className="px-4 py-4 custom-scrollbar bg-body-color">
                    <Form>
                        <Row className="mb-4 g-3">
                            <Col md={12}>
                                <label className="ts-modal-label text-uppercase fw-bold text-white mb-2 ruleset-modal__label">
                                    {t(RULESETS_LABEL_NAME, 'Name')} *
                                </label>
                                <Form.Control
                                    placeholder={t(RULESETS_NAME_PLACEHOLDER, 'e.g. security-baseline')}
                                    value={form.name}
                                    onChange={(e) => patch({ name: e.target.value })}
                                    style={{ fontSize: '13px', height: '40px' }}
                                    className="rule-search-input"
                                    autoFocus
                                />
                            </Col>
                        </Row>

                        <Row className="mb-4 g-3">
                            <Col md={12}>
                                <label className="ts-modal-label text-uppercase fw-bold text-white mb-2 ruleset-modal__label">
                                    {t(RULESETS_LABEL_CONNECTOR, 'Connector')}
                                </label>
                                <div style={{ height: '40px' }} className="rule-search-input rounded">
                                    <DropdownComponent
                                        value={
                                            form.cid
                                                ? connectorMap[form.cid] ?? form.cid
                                                : t(RULESETS_NONE_OPTION, '— None —')
                                        }
                                        dropDownItems={connectorDropdownItems}
                                        onChange={(eventKey) => patch({ cid: eventKey ?? '' })}
                                        variant="filled"
                                        isStartCase={false}
                                        disableToggle={!!editingRuleSet}
                                    />
                                </div>
                            </Col>
                        </Row>

                        <Row className="mb-4 g-3">
                            <Col md={12}>
                                <label className="ts-modal-label text-uppercase fw-bold text-white mb-2 ruleset-modal__label">
                                    {t(RULESETS_LABEL_DESCRIPTION, 'Description')}
                                </label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={form.description}
                                    onChange={(e) => patch({ description: e.target.value })}
                                    placeholder={t(RULESETS_DESC_PLACEHOLDER, 'Optional description…')}
                                    className="rule-search-input rulesets-config__textarea"
                                />
                            </Col>
                        </Row>

                        <Row className="mb-2 g-3">
                            <Col md={12}>
                                <div
                                    className="d-flex align-items-center gap-3 px-3 py-2 rounded ruleset-modal__default-toggle"
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    // eslint-disable-next-line camelcase
                                    onClick={() => patch({ is_default: !form.is_default })}
                                >
                                    <div
                                        className={`ruleset-modal__toggle-track ruleset-modal__toggle-track--${form.is_default ? 'on' : 'off'}`}
                                    >
                                        <div className="ruleset-modal__toggle-thumb" />
                                    </div>
                                    <div className="d-flex flex-column">
                                        <span className="ruleset-modal__label fw-bold text-white">
                                            {t(RULESETS_DEFAULT_TOGGLE_LABEL, 'Set as Default Rule Set')}
                                        </span>
                                        <span className="ruleset-modal__default-toggle-hint">
                                            {form.is_default
                                                ? t(RULESETS_DEFAULT_TOGGLE_ON_HINT, 'This rule set will become the default for its connector')
                                                : t(RULESETS_DEFAULT_TOGGLE_OFF_HINT, 'Make this the default rule set for its connector')}
                                        </span>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Form>
                </Modal.Body>

                <Modal.Footer className="border-secondary px-4 py-3 ruleset-modal__footer">
                    <Button
                        disabled={saving}
                        onClick={handleCloseModal}
                        className="ruleset-modal__cancel-btn"
                    >
                        {t(RULESETS_CANCEL_BTN, '✕ Cancel')}
                    </Button>
                    <Button
                        disabled={!isFormValid || saving}
                        onClick={handleSave}
                        className={`ruleset-modal__save-btn ruleset-modal__save-btn--${!isFormValid || saving ? 'disabled' : 'active'}`}
                    >
                        {saving
                            ? t(RULESETS_SAVING, 'Saving…')
                            : editingRuleSet
                            ? t(RULESETS_SAVE_CHANGES, 'Save Changes')
                            : t(RULESETS_CREATE_BTN, 'Create Rule Set')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default RuleSetsConfig;
